import { useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { CreateUserFormData } from '@/lib/validation/userSchema';
import { DEFAULT_TEMP_PASSWORD } from '@/lib/auth/passwordUtils';
import { useUserStore } from '@/store/userStore';

export const useCreateUser = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchUsers } = useUserStore();

  const createUser = async (userData: CreateUserFormData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get current session for authorization
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('You must be logged in to create users');
      }

      // Call the Edge Function to create user (uses admin API, doesn't affect current session)
      const response = await supabase.functions.invoke('create-user', {
        body: {
          email: userData.email,
          password: DEFAULT_TEMP_PASSWORD,
          role: userData.role,
          full_name: userData.firstName,
          mobile_number: userData.mobileNumber || null,
        },
      });

      // Check response.data.error first — the edge function returns detailed error JSON
      // response.error is a generic FunctionsHttpError ("non-2xx status code") that masks the real message
      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      if (response.error) {
        throw new Error(response.error.message || 'Failed to create user');
      }

      // Wait for the database trigger to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Fetch updated users list
      await fetchUsers();

      return {
        user: response.data?.user,
        tempPassword: DEFAULT_TEMP_PASSWORD
      };
    } catch (err) {
      console.error('Error creating user:', err);
      const message = err instanceof Error ? err.message : 'Failed to create user';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { createUser, isLoading, error };
};